// src/views/admin/ADMessages.jsx
//
// PERMISSIONS (module "messages") :
// - messages.voir              : accès à la page (déjà géré sidebar / route)
// - messages.envoyer_parents   : bouton "Nouveau message" + zone de saisie / pièce jointe
//                                dans les conversations avec les parents
// - messages.envoyer_interne   : zone de saisie de la messagerie interne
//
// Les deux onglets restent visibles pour qui a accès à la page ; sans
// permission d'envoi, les conversations sont en lecture seule.

import { useState, useEffect, useRef } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { useAuth } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionsContext";
import { urlimage } from "../../utils/config";

function Modal({ title, onClose, children, maxWidth = 480 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const ROLE_LABELS = {
  PARENT: { label: "Parent", cls: "pu" },
  ENSEIGNANT: { label: "Enseignant", cls: "bl" },
  ADMINISTRATEUR: { label: "Administrateur", cls: "neu" },
};

function formatDateTime(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("fr-FR"); } catch { return d; }
}

function initialsOf(name) {
  return (name || "??").slice(0, 2).toUpperCase();
}

// Détermine si un message a été envoyé par l'utilisateur connecté.
// On compare d'abord par username (le plus fiable, comme dans PAMessages.jsx),
// puis par id en le castant en string (id peut être number côté user et
// string côté expediteur, ou inversement, selon l'endpoint), puis en dernier
// recours par rôle : dans un fil admin ↔ parent, tout ce qui ne vient pas du
// parent est censé venir de l'administrateur connecté.
function isMineMessage(m, user) {
  if (!m) return false;
  if (user?.username && m.expediteur?.username) {
    return String(m.expediteur.username) === String(user.username);
  }
  if (user?.id != null && m.expediteur?.id != null) {
    return String(m.expediteur.id) === String(user.id);
  }
  if (m.expediteur?.role) {
    return m.expediteur.role !== "PARENT";
  }
  return false;
}

// ─── HELPERS PIÈCES JOINTES (repris de PAMessages.jsx) ────────────────────
function getFullUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${urlimage}${path}`;
}

function isImageFile(fileName) {
  if (!fileName) return false;
  const exts = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
  return exts.some(ext => fileName.toLowerCase().endsWith(ext));
}

function isVideoFile(fileName) {
  if (!fileName) return false;
  const exts = [".mp4", ".mov", ".avi", ".mkv", ".webm", ".3gp", ".m4v"];
  return exts.some(ext => fileName.toLowerCase().endsWith(ext));
}

function getFileIconClass(fileName) {
  if (!fileName) return "ti-file";
  const name = fileName.toLowerCase();
  if (isVideoFile(name)) return "ti-file-video";
  if (name.endsWith(".pdf")) return "ti-file-type-pdf";
  if (name.endsWith(".doc") || name.endsWith(".docx")) return "ti-file-type-doc";
  if (name.endsWith(".xls") || name.endsWith(".xlsx")) return "ti-file-spreadsheet";
  if (name.endsWith(".ppt") || name.endsWith(".pptx")) return "ti-presentation";
  if (name.endsWith(".mp3") || name.endsWith(".wav")) return "ti-file-music";
  if (name.endsWith(".zip") || name.endsWith(".rar")) return "ti-file-zip";
  return "ti-file";
}

function getFileName(url) {
  if (!url) return "Fichier joint";
  const parts = url.split("/");
  let fileName = parts[parts.length - 1];
  const queryIndex = fileName.indexOf("?");
  if (queryIndex !== -1) fileName = fileName.substring(0, queryIndex);
  if (fileName.length > 20) {
    const ext = fileName.split(".").pop();
    const name = fileName.substring(0, 15);
    return `${name}...${ext}`;
  }
  return fileName;
}

// Rendu d'une pièce jointe reçue dans une bulle (image / vidéo / document)
function renderPieceJointe(fileUrl, mine) {
  if (!fileUrl) return null;
  const fullUrl = getFullUrl(fileUrl);
  const fileName = getFileName(fullUrl);

  if (isImageFile(fullUrl)) {
    return (
      <img
        src={fullUrl}
        alt="Pièce jointe"
        onClick={() => window.open(fullUrl, "_blank")}
        onError={e => { e.target.style.display = "none"; }}
        style={{
          width: "100%",
          maxHeight: 200,
          borderRadius: 8,
          objectFit: "cover",
          marginTop: 6,
          cursor: "pointer",
          display: "block",
        }}
      />
    );
  }

  if (isVideoFile(fullUrl)) {
    return (
      <div
        onClick={() => window.open(fullUrl, "_blank")}
        style={{
          marginTop: 6,
          background: "#1f2937",
          borderRadius: 8,
          height: 140,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          cursor: "pointer",
          color: "#fff",
        }}
      >
        <i className="ti ti-player-play" style={{ fontSize: 28 }} />
        <span style={{ fontSize: 11 }}>{fileName}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => window.open(fullUrl, "_blank")}
      style={{
        marginTop: 6,
        width: "100%",
        padding: 8,
        background: mine ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.04)",
        border: "none",
        borderRadius: 8,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "inherit",
        fontFamily: "inherit",
      }}
    >
      <i className={`ti ${getFileIconClass(fileName)}`} style={{ fontSize: 18 }} />
      <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {fileName}
        </div>
        <div style={{ fontSize: 10, opacity: 0.7 }}>Document</div>
      </div>
      <i className="ti ti-external-link" />
    </button>
  );
}

// Bandeau affiché à la place de la zone de saisie quand l'envoi n'est pas autorisé
function LectureSeuleNotice() {
  return (
    <div style={{ padding: 10, borderTop: "0.5px solid var(--b)", fontSize: 13, color: "var(--h)", textAlign: "center" }}>
      <i className="ti ti-lock" style={{ marginRight: 6 }} />
      Vous n'avez pas la permission d'envoyer des messages ici (lecture seule).
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────
export default function ADMessages({ slug }) {
  const [activeTab, setActiveTab] = useState("inbox");
  const [unreadCount, setUnreadCount] = useState(null);

  const { getMessagesNonLusCount } = useAdminService();

  useEffect(() => { if (slug) refreshUnread(); }, [slug]);

  const refreshUnread = async () => {
    try {
      const res = await getMessagesNonLusCount(slug);
      setUnreadCount(res.non_lus);
    } catch (error) {
      // silencieux, non bloquant
    }
  };

  return (
    <div>
      {/* Onglets comme dans Configuration */}
      <div className="tab-nav">
        <button
          className={`tab-btn ${activeTab === "inbox" ? "active" : ""}`}
          onClick={() => setActiveTab("inbox")}
        >
          <i className="ti ti-inbox" /> BOÎTE DE RÉCEPTION
          {unreadCount > 0 && (
            <span className="pill err" style={{ marginLeft: 4, fontSize: 11 }}>{unreadCount}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === "interne" ? "active" : ""}`}
          onClick={() => setActiveTab("interne")}
        >
          <i className="ti ti-users-group" /> MESSAGERIE INTERNE
        </button>
      </div>


      {activeTab === "inbox" && <InboxPanel slug={slug} onRead={refreshUnread} />}
      {activeTab === "interne" && <MessagerieInternePanel slug={slug} />}

      <style>{`
/* ─── ONGLETS (thème bleu + or) ─── */
.tab-nav {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
  border-radius: 10px;
  border-bottom: 3px solid var(--or);
  padding: 8px 12px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  color: rgba(255,255,255,0.75);
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.tab-btn i { font-size: 18px; }

.tab-btn:hover {
  background: rgba(255,255,255,0.12);
  color: var(--or-lt);
}

.tab-btn.active {
  background: white;
  color: var(--pr);
  box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
}
.tab-btn.active i { color: var(--or); }
.tab-btn.active:hover { background: white; color: var(--pr); }

        .pill {
          display: inline-block;
          padding: 4px 9px;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 500;
        }
        .pill.ok  { background: var(--gn-bg); color: var(--gn); }
        .pill.warn{ background: var(--am-bg); color: var(--am); }
        .pill.err { background: var(--rd-bg); color: var(--rd); }
        .pill.info{ background: var(--bl-bg); color: var(--bl-dk); }
        .pill.pu  { background: var(--pr-bg); color: var(--pr); }
        .pill.neu { background: var(--s2); color: var(--m); }

        .card {
          background: var(--s);
          border: 0.5px solid var(--b);
          border-radius: var(--radius-md);
          padding: 14px;
        }

        .bprim {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 15px;
          background: var(--pr);
          color: #fff;
          border: none;
          border-radius: 5px;
          font-size: 15px;
          cursor: pointer;
          font-family: inherit;
          font-weight: 500;
          white-space: nowrap;
          transition: opacity .12s;
        }
        .bprim:hover { opacity: .88; }
        .bprim.rd { background: var(--rd); }

        .bsec {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 13px;
          background: var(--s);
          color: var(--m);
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          cursor: pointer;
          font-family: inherit;
          transition: all .12s;
        }
        .bsec:hover { background: var(--s2); color: var(--t); }

        .sb2 {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 11px;
          flex-wrap: wrap;
        }
        .sb2 input {
          flex: 1;
          min-width: 120px;
          padding: 8px 13px;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
        }
        .sb2 select {
          padding: 8px 13px;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
        }

        .factions {
          display: flex;
          gap: 7px;
          margin-top: 12px;
          justify-content: flex-end;
        }

        .av {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 12px;
          flex-shrink: 0;
        }
        .av.pu { background: var(--pr-bg); color: var(--pr); }
        .av.bl { background: var(--bl-bg); color: var(--bl-dk); }
        .av.info { background: var(--bl-bg); color: var(--bl-dk); }
        .av.neu { background: var(--s2); color: var(--m); }

        .ibtn {
          width: 30px;
          height: 30px;
          border-radius: 5px;
          border: 0.5px solid var(--b);
          background: var(--s);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: var(--m);
          font-size: 17px;
          transition: all .12s;
        }
        .ibtn:hover { background: var(--s2); color: var(--t); }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.35);
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
        }
        .modal-box {
          background: var(--s);
          border-radius: var(--radius-lg);
          border: 0.5px solid var(--b);
          padding: 18px;
          max-width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-title {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin-bottom: 13px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .fg {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .fg label {
          font-size: 14px;
          color: var(--m);
          font-weight: 500;
        }
        .fg input, .fg select, .fg textarea {
          padding: 9px 13px;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
          width: 100%;
        }
        .fg textarea {
          resize: vertical;
          min-height: 56px;
        }

        @media (max-width: 768px) {
          .tab-btn { padding: 6px 10px; font-size: 11px; }
          .tab-btn i { font-size: 14px; }
        }
        @media (max-width: 576px) {
          .tab-btn span { display: none; }
          .tab-btn i { font-size: 18px; margin: 0; }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// BOÎTE DE RÉCEPTION (conversations avec les parents)
// ═══════════════════════════════════════════════════════════════════════
function InboxPanel({ slug, onRead }) {
  const { canSee } = usePermissions();
  const peutEnvoyerParents = canSee("messages.envoyer_parents");

  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNouveau, setShowNouveau] = useState(false);

  const toast = useToastHook();
  const { getMessagesRecus } = useAdminService();

  useEffect(() => { if (slug) loadConversations(); }, [slug]);

  const loadConversations = async () => {
    setLoadingConvs(true);
    try {
      const data = await getMessagesRecus(slug);
      setConversations(data || []);
    } catch (error) {
      toast.error("Impossible de charger les conversations");
    } finally {
      setLoadingConvs(false);
    }
  };

  const conversationsFiltrees = conversations.filter(c => {
    const q = search.toLowerCase();
    if (!q) return true;
    const nom = `${c.expediteur?.username || ""} ${c.enfants_classe || ""}`.toLowerCase();
    return nom.includes(q);
  });

  const handleSelect = (c) => {
    setSelected(c.expediteur);
    setConversations(prev => prev.map(x =>
      x.expediteur.id === c.expediteur.id ? { ...x, messages_non_lus: 0 } : x
    ));
  };

  const handleNouveauCree = (parentUtilisateur) => {
    setShowNouveau(false);
    setSelected(parentUtilisateur);
    loadConversations();
  };

  return (
    // minWidth: 0 sur les enfants de la grille : évite que la colonne "1fr"
    // (le fil de discussion) ne se retrouve calculée trop étroite, ce qui
    // forçait le texte à retourner à la ligne mot par mot / lettre par lettre.
    <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 12, alignItems: "start" }}>
      {/* Liste des conversations */}
      <div className="card" style={{ padding: 0, overflow: "hidden", minWidth: 0 }}>
        <div style={{ padding: 10, borderBottom: "0.5px solid var(--b)" }}>
          <div className="sb2" style={{ marginBottom: peutEnvoyerParents ? 8 : 0 }}>
            <input type="text" placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {/* messages.envoyer_parents */}
          {peutEnvoyerParents && (
            <button className="bprim" style={{ width: "100%", justifyContent: "center" }} onClick={() => setShowNouveau(true)}>
              <i className="ti ti-plus" /> Nouveau message
            </button>
          )}
        </div>

        {loadingConvs ? <Loader /> : (
          <div style={{ maxHeight: 560, overflowY: "auto" }}>
            {conversationsFiltrees.map(c => {
              const role = ROLE_LABELS[c.expediteur?.role] || ROLE_LABELS.PARENT;
              const active = selected?.id === c.expediteur?.id;
              return (
                <div
                  key={c.expediteur.id}
                  onClick={() => handleSelect(c)}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    borderBottom: "0.5px solid var(--b)",
                    background: active ? "var(--pr-bg)" : "transparent",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <div className={`av ${role.cls}`}>{initialsOf(c.expediteur?.username)}</div>
                      <div style={{ fontWeight: 500, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.expediteur?.username}
                      </div>
                    </div>
                    {c.messages_non_lus > 0 && <span className="pill err" style={{ fontSize: 11 }}>{c.messages_non_lus}</span>}
                  </div>
                  {c.enfants_classe && (
                    <div style={{ fontSize: 12, color: "var(--m)", marginTop: 2 }}>{c.enfants_classe}</div>
                  )}
                  <div style={{ fontSize: 13, color: "var(--m)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.is_my_last_message && <span>Vous : </span>}
                    {c.dernier_message}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--h)", marginTop: 2 }}>{formatDateTime(c.dernier_message_date)}</div>
                </div>
              );
            })}
            {conversationsFiltrees.length === 0 && (
              <div style={{ textAlign: "center", color: "var(--h)", padding: 24, fontSize: 13 }}>
                Aucune conversation
              </div>
            )}
          </div>
        )}
      </div>

      {/* Thread */}
      {selected ? (
        <ParentThread
          slug={slug}
          parent={selected}
          onMessageSent={() => { loadConversations(); onRead(); }}
        />
      ) : (
        <div className="card" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, minWidth: 0, color: "var(--h)" }}>
          <i className="ti ti-message-circle" style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }} />
          <div>Sélectionne une conversation pour l'ouvrir</div>
        </div>
      )}

      {peutEnvoyerParents && showNouveau && (
        <NouveauMessageModal
          slug={slug}
          onClose={() => setShowNouveau(false)}
          onCree={handleNouveauCree}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PARENT THREAD - Conversation avec un parent
// ═══════════════════════════════════════════════════════════════════════
function ParentThread({ slug, parent, onMessageSent }) {
  const { canSee } = usePermissions();
  const peutEnvoyerParents = canSee("messages.envoyer_parents");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contenu, setContenu] = useState("");
  const [pieceJointe, setPieceJointe] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const { user } = useAuth();

  const toast = useToastHook();
  const { getMessagerieParent, postMessagerieParent } = useAdminService();

  useEffect(() => { load(); }, [slug, parent?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMessagerieParent(slug, parent.id);
      setData(res);
    } catch (error) {
      toast.error("Impossible de charger la conversation");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!peutEnvoyerParents) return;
    if (!contenu.trim() && !pieceJointe) return;
    setSending(true);
    try {
      await postMessagerieParent(slug, parent.id, { contenu }, pieceJointe);
      setContenu("");
      setPieceJointe(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
      onMessageSent();
    } catch (error) {
      toast.error("Impossible d'envoyer le message");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="card" style={{ minHeight: 400, minWidth: 0 }}><Loader /></div>;

  const role = ROLE_LABELS[parent.role] || ROLE_LABELS.PARENT;

  return (
    // minWidth: 0 + width: 100% : la carte est un enfant de grille ("1fr"),
    // sans ça le navigateur peut la laisser à sa largeur de contenu minimale
    // et écraser les bulles de message (texte qui retombe à la verticale).
    <div className="card" style={{ padding: 0, display: "flex", flexDirection: "column", height: 560, minWidth: 0, width: "100%", boxSizing: "border-box" }}>
      {/* En-tête du thread */}
      <div style={{ padding: 12, borderBottom: "0.5px solid var(--b)", display: "flex", alignItems: "center", gap: 10 }}>
        <div className={`av ${role.cls}`} style={{ width: 36, height: 36, fontSize: 14 }}>
          {initialsOf(parent.username)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{parent.username}</div>
          <div style={{ fontSize: 12, color: "var(--m)" }}>
            <span className={`pill ${role.cls}`} style={{ marginRight: 6, fontSize: 11 }}>{role.label}</span>
            {(data?.eleves || []).map(e => `${e.prenom} ${e.nom}`).join(", ")}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8, minWidth: 0, width: "100%", boxSizing: "border-box" }}>
        {(data?.messages || []).map(m => {
          const mine = isMineMessage(m, user);
          // Le back peut renvoyer le champ sous des noms différents selon
          // l'endpoint : on couvre les deux.
          const fichier = m.piece_jointe_url || m.piece_jointe;
          return (
            <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", width: "100%" }}>
              <div style={{
                maxWidth: "70%",
                minWidth: 0,
                boxSizing: "border-box",
                padding: "8px 11px",
                borderRadius: 10,
                background: mine ? "var(--pr)" : "var(--s2)",
                color: mine ? "#fff" : "var(--t)",
              }}>
                {m.contenu && (
                  <div style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.contenu}</div>
                )}
                {fichier && renderPieceJointe(fichier, mine)}
                <div style={{ fontSize: 11, opacity: 0.75, marginTop: 3, textAlign: "right" }}>
                  {formatDateTime(m.date_envoi)}
                </div>
              </div>
            </div>
          );
        })}
        {(data?.messages || []).length === 0 && (
          <div style={{ textAlign: "center", color: "var(--h)", padding: 24, fontSize: 14 }}>
            Aucun message pour le moment
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Zone de saisie — messages.envoyer_parents */}
      {peutEnvoyerParents ? (
        <div style={{ padding: 10, borderTop: "0.5px solid var(--b)" }}>
          <div className="sb2" style={{ marginBottom: 0 }}>
            <input
              type="text"
              placeholder="Écrire un message…"
              value={contenu}
              onChange={e => setContenu(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              style={{ flex: 1 }}
            />
            <button className="ibtn" title="Joindre un fichier" onClick={() => fileInputRef.current?.click()}>
              <i className="ti ti-paperclip" />
            </button>
            <input
              ref={fileInputRef} type="file" style={{ display: "none" }}
              onChange={e => setPieceJointe(e.target.files?.[0] || null)}
            />
            <button className="bprim" disabled={sending || (!contenu.trim() && !pieceJointe)} onClick={handleSend}>
              <i className="ti ti-send" /> Envoyer
            </button>
          </div>
          {pieceJointe && (
            <div style={{ fontSize: 12, color: "var(--m)", marginTop: 6 }}>
              <i className="ti ti-paperclip" /> {pieceJointe.name}
              <button className="ibtn" style={{ border: "none", width: 18, height: 18, marginLeft: 4 }} onClick={() => setPieceJointe(null)}>
                <i className="ti ti-x" style={{ fontSize: 12 }} />
              </button>
            </div>
          )}
        </div>
      ) : (
        <LectureSeuleNotice />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// NOUVEAU MESSAGE MODAL
// ═══════════════════════════════════════════════════════════════════════
function NouveauMessageModal({ slug, onClose, onCree }) {
  const [classes, setClasses] = useState([]);
  const [classeId, setClasseId] = useState("");
  const [eleves, setEleves] = useState([]);
  const [eleveId, setEleveId] = useState("");
  const [contenu, setContenu] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingEleves, setLoadingEleves] = useState(false);
  const [sending, setSending] = useState(false);

  const toast = useToastHook();
  const { getNouveauMessageData, postMessagerieParent } = useAdminService();

  useEffect(() => { init(); }, []);
  useEffect(() => { if (classeId) loadEleves(); else setEleves([]); }, [classeId]);

  const init = async () => {
    setLoading(true);
    try {
      const res = await getNouveauMessageData(slug);
      setClasses(res.classes || []);
    } catch (error) {
      toast.error("Impossible de charger les classes");
    } finally {
      setLoading(false);
    }
  };

  const loadEleves = async () => {
    setLoadingEleves(true);
    setEleveId("");
    try {
      const res = await getNouveauMessageData(slug, { classe_id: classeId });
      setEleves(res.eleves || []);
    } catch (error) {
      toast.error("Impossible de charger les élèves de cette classe");
    } finally {
      setLoadingEleves(false);
    }
  };

  const eleveSelectionne = eleves.find(e => String(e.id) === String(eleveId));
  const parent = eleveSelectionne?.parent;

  const handleEnvoyer = async () => {
    if (!parent) { toast.error("Sélectionne un élève dont le parent est renseigné"); return; }
    if (!contenu.trim()) { toast.error("Écris un message"); return; }
    setSending(true);
    try {
      await postMessagerieParent(slug, parent.id, { contenu, eleve_id: eleveSelectionne.id });
      toast.success("Message envoyé");
      onCree(parent);
    } catch (error) {
      toast.error("Impossible d'envoyer le message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title="Nouveau message" onClose={onClose} maxWidth={480}>
      {loading ? <Loader /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <div className="fg">
            <label>Classe</label>
            <select value={classeId} onChange={e => setClasseId(e.target.value)}>
              <option value="">-- Choisir une classe --</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>

          {classeId && (
            <div className="fg">
              <label>Élève</label>
              {loadingEleves ? <Loader /> : (
                <select value={eleveId} onChange={e => setEleveId(e.target.value)}>
                  <option value="">-- Choisir un élève --</option>
                  {eleves.map(e => <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>)}
                </select>
              )}
            </div>
          )}

          {eleveSelectionne && !parent && (
            <div style={{ fontSize: 13, color: "var(--rd)" }}>
              <i className="ti ti-alert-circle" /> Aucun parent associé à cet élève.
            </div>
          )}
          {parent && (
            <div style={{ fontSize: 13, color: "var(--m)" }}>
              Destinataire : <strong>{parent.username}</strong>
            </div>
          )}

          <div className="fg">
            <label>Message</label>
            <textarea rows={4} value={contenu} onChange={e => setContenu(e.target.value)} />
          </div>
        </div>
      )}
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={sending}>Annuler</button>
        <button className="bprim" onClick={handleEnvoyer} disabled={sending || !parent}>
          {sending ? "..." : <><i className="ti ti-send" /> Envoyer</>}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MESSAGERIE INTERNE
// ═══════════════════════════════════════════════════════════════════════
function MessagerieInternePanel({ slug }) {
  const { canSee } = usePermissions();
  const peutEnvoyerInterne = canSee("messages.envoyer_interne");

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contenu, setContenu] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const { user } = useAuth();

  const toast = useToastHook();
  const { getMessagerieAdmin, postMessagerieAdmin } = useAdminService();

  useEffect(() => { if (slug) load(); }, [slug]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getMessagerieAdmin(slug);
      setMessages(data || []);
    } catch (error) {
      toast.error("Impossible de charger la messagerie interne");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!peutEnvoyerInterne) return;
    if (!contenu.trim()) return;
    setSending(true);
    try {
      await postMessagerieAdmin(slug, { contenu });
      setContenu("");
      await load();
    } catch (error) {
      toast.error("Impossible d'envoyer le message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card" style={{ padding: 0, display: "flex", flexDirection: "column", height: 560, maxWidth: 700, minWidth: 0, width: "100%", boxSizing: "border-box" }}>
      <div style={{ padding: 12, borderBottom: "0.5px solid var(--b)", fontSize: 13, color: "var(--m)" }}>
        <i className="ti ti-info-circle" style={{ marginRight: 6 }} />
        Messages visibles par toute l'équipe administrative de l'établissement.
      </div>

      {loading ? <Loader /> : (
        <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8, minWidth: 0, width: "100%", boxSizing: "border-box" }}>
          {messages.map(m => {
            const mine = isMineMessage(m, user);
            return (
              <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", width: "100%" }}>
                <div style={{
                  maxWidth: "75%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  padding: "8px 11px",
                  borderRadius: 10,
                  background: mine ? "var(--pr)" : "var(--s2)",
                  color: mine ? "#fff" : "var(--t)",
                }}>
                  {!mine && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{m.expediteur?.username}</div>}
                  <div style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.contenu}</div>
                  <div style={{ fontSize: 11, opacity: 0.75, marginTop: 3, textAlign: "right" }}>
                    {formatDateTime(m.date_envoi)}
                  </div>
                </div>
              </div>
            );
          })}
          {messages.length === 0 && (
            <div style={{ textAlign: "center", color: "var(--h)", padding: 24, fontSize: 14 }}>
              <i className="ti ti-message" style={{ fontSize: 32, display: "block", marginBottom: 8, opacity: 0.4 }} />
              Aucun message
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Zone de saisie — messages.envoyer_interne */}
      {peutEnvoyerInterne ? (
        <div style={{ padding: 10, borderTop: "0.5px solid var(--b)" }}>
          <div className="sb2" style={{ marginBottom: 0 }}>
            <input
              type="text"
              placeholder="Écrire à l'équipe…"
              value={contenu}
              onChange={e => setContenu(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              style={{ flex: 1 }}
            />
            <button className="bprim" disabled={sending || !contenu.trim()} onClick={handleSend}>
              <i className="ti ti-send" /> Envoyer
            </button>
          </div>
        </div>
      ) : (
        <LectureSeuleNotice />
      )}
    </div>
  );
}